
class GiftOptions extends HTMLElement {

  connectedCallback() {
    this.setupEngraving();
    this.setupEngravingReview();
    this.setupGiftWrap();
  }

  setupEngraving() {
    this.engravingRow = this.querySelector('[data-engraving-row]');
    this.engravingToggle = this.querySelector('[data-engraving-toggle]');
    this.engravingSummary = this.querySelector('[data-engraving-summary]');
    this.engravingValue = this.querySelector('[data-engraving-value]');
    this.engravingModal = this.querySelector('[data-engraving-modal]');
    this.engravingOverlay = this.querySelector('[data-engraving-overlay]');
    this.engravingRemove = this.querySelector('[data-engraving-remove]');
    this.engravingEdit = this.querySelector('[data-engraving-edit]');
    this.engravingHiddenLines = Array.from(this.querySelectorAll('[data-engraving-hidden-line]'));
    this.engravingHiddenStyle = this.querySelector('[data-engraving-hidden-style]');
    this.engravingHiddenId = this.querySelector('[data-engraving-hidden-id]');
    this.engravingHiddenFeeVariantId = this.querySelector('[data-engraving-hidden-fee-variant-id]');
    this.engravingModalRemove = this.engravingModal ? this.engravingModal.querySelector('[data-engraving-modal-remove]') : null;
    this.engravingSave = this.engravingModal
      ? this.engravingModal.querySelector('[data-engraving-save]')
      : null;
    this.engravingLineInputs = this.engravingModal
      ? Array.from(this.engravingModal.querySelectorAll('[data-engraving-line-input]'))
      : [];
    this.engravingCanvas = this.engravingModal ? this.engravingModal.querySelector('[data-engraving-canvas]') : null;
    this.engravingCanvasImage = null;
    this.engravingCanvasImageLoaded = false;
    this.engravingStyleButtons = this.engravingModal
      ? Array.from(this.engravingModal.querySelectorAll('[data-engraving-style-option]'))
      : [];
    this.engravingStyleName = this.engravingModal
      ? this.engravingModal.querySelector('[data-engraving-style-name]')
      : null;
    this.engravingTermsCheckbox = this.engravingModal
      ? this.engravingModal.querySelector('[data-engraving-terms-checkbox]')
      : null;
    this.engravingErrorBox = this.engravingModal
      ? this.engravingModal.querySelector('[data-engraving-error]')
      : null;
    this.engravingTermsErrorBox = this.querySelector('[data-engraving-terms-error]');
    this.engravingSelectedStyle = 'block';

    if (!this.engravingToggle) return;

    this.relocateDrawer(this.engravingModal, this.engravingOverlay);
    this.loadEngravingCanvasImage();
    this.loadEngravingCanvasFonts();

    window.addEventListener('resize', () => {
      if (this.engravingModal && this.engravingModal.hasAttribute('open')) {
        this.resizeEngravingCanvas();
        this.drawEngravingPreview();
      }
    });

    this.engravingToggle.addEventListener('click', (event) => this.openEngravingModal(event.currentTarget));
    if (this.engravingEdit)
      this.engravingEdit.addEventListener('click', (event) => this.openEngravingModal(event.currentTarget));
    if (this.engravingRemove) this.engravingRemove.addEventListener('click', () => this.clearEngraving());
    if (this.engravingSave) this.engravingSave.addEventListener('click', () => this.saveEngraving());

    this.engravingLineInputs.forEach((input) => {
      input.addEventListener('input', () => {
        this.showEngravingError(null);
        this.drawEngravingPreview();
        this.updateEngravingSaveState();
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.saveEngraving();
        }
      });
    });

    this.engravingStyleButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectEngravingStyle(button));
    });

    if (this.engravingTermsCheckbox) {
      this.engravingTermsCheckbox.addEventListener('change', () => {
        this.showEngravingError(null);
        this.showEngravingTermsError(null);
        this.updateEngravingSaveState();
      });
    }

    this.updateEngravingSaveState();

    if (this.engravingModalRemove) {
      this.engravingModalRemove.addEventListener('click', () => {
        this.closeEngravingModal();
        this.clearEngraving();
      });
    }

    if (this.engravingModal) {
      this.engravingModal.querySelectorAll('[data-engraving-modal-close]').forEach((element) => {
        element.addEventListener('click', () => this.closeEngravingModal());
      });
    }
    if (this.engravingOverlay) {
      this.engravingOverlay.addEventListener('click', () => this.closeEngravingModal());
    }
  }

  selectEngravingStyle(button) {
    const style = button.dataset.engravingStyleOption;
    this.engravingSelectedStyle = style;

    this.engravingStyleButtons.forEach((styleButton) => {
      const isSelected = styleButton === button;
      styleButton.classList.toggle('is-selected', isSelected);
      styleButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    if (this.engravingStyleName) {
      this.engravingStyleName.textContent = button.dataset.engravingStyleLabel || button.textContent.trim();
    }
    this.drawEngravingPreview();
  }

  loadEngravingCanvasImage() {
    if (!this.engravingCanvas) return;
    const src = this.engravingCanvas.dataset.engravingImage;
    if (!src) return;

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      this.engravingCanvasImage = image;
      this.engravingCanvasImageLoaded = true;
      this.drawEngravingPreview();
    };
    image.src = src;
  }

  loadEngravingCanvasFonts() {
    if (!document.fonts || !document.fonts.load) return;
    Promise.all([document.fonts.load('16px "Gideon Roman"'), document.fonts.load('16px "Monsieur La Doulaise"')])
      .then(() => this.drawEngravingPreview())
      .catch(() => { });
  }

  getEngravingCanvasFont(style) {
    if (style === 'roman') return '"Gideon Roman", Georgia, serif';
    if (style === 'cursive') return '"Monsieur La Doulaise", "Brush Script MT", cursive';
    return "'Montserrat', sans-serif";
  }

  // Sizes the canvas backing store to its real displayed CSS size (times
  // devicePixelRatio for sharpness) and scales the context so every drawing
  // command below can use real CSS-pixel values directly - e.g. a 14px font
  // actually renders as 14 on-screen pixels, matching the design spec.
  resizeEngravingCanvas() {
    if (!this.engravingCanvas) return;
    const rect = this.engravingCanvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const dpr = window.devicePixelRatio || 1;
    this.engravingCanvasWidth = rect.width;
    this.engravingCanvasHeight = rect.height;
    this.engravingCanvas.width = Math.round(rect.width * dpr);
    this.engravingCanvas.height = Math.round(rect.height * dpr);

    const ctx = this.engravingCanvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  drawEngravingPreview() {
    if (!this.engravingCanvas) return;
    if (!this.engravingCanvasWidth) this.resizeEngravingCanvas();
    if (!this.engravingCanvasWidth) return;

    const ctx = this.engravingCanvas.getContext('2d');
    const width = this.engravingCanvasWidth;
    const height = this.engravingCanvasHeight;

    ctx.clearRect(0, 0, width, height);

    if (this.engravingCanvasImageLoaded && this.engravingCanvasImage) {
      const image = this.engravingCanvasImage;

      const imageWidth = image.naturalWidth || image.width;
      const imageHeight = image.naturalHeight || image.height;

      // Contain-fit the image so the complete image is visible
      // without cropping or unwanted zoom.
      const imageAreaWidth = width;
      const imageAreaHeight = height;

      const scale = Math.min(
        imageAreaWidth / imageWidth,
        imageAreaHeight / imageHeight
      );

      const drawWidth = imageWidth * scale;
      const drawHeight = imageHeight * scale;

      const offsetX = (width - drawWidth) / 2;
      const offsetY = (height - drawHeight) / 2;

      ctx.drawImage(
        image,
        offsetX,
        offsetY,
        drawWidth,
        drawHeight
      );
    }

    const placeholderText = this.engravingCanvas.dataset.engravingPlaceholder || 'Your text';
    const lines = this.engravingLineInputs.map((input) => input.value.trim() || placeholderText);
    if (!lines.length) return;

    // Typography spec: 600 weight, 19px (14px + 5px), 0% letter-spacing,
    // centered both horizontally and vertically, 10px gap between lines.
    const fontSize = 12;
    const lineGap = 10;
    const lineHeight = fontSize + lineGap;
    const fontFamily = this.getEngravingCanvasFont(this.engravingSelectedStyle);

    ctx.fillStyle = '#C0C0C0';
    ctx.textBaseline = 'middle';
    ctx.font = `600 ${fontSize}px ${fontFamily}`;
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';

    const totalHeight = lineHeight * (lines.length - 1);
    const startY = height / 2 - totalHeight / 2;

    const extraNudgeByStyle = { cursive: 8, roman: 8 };
    const horizontalNudge = 5 + (extraNudgeByStyle[this.engravingSelectedStyle] || 0);

    lines.forEach((line, index) => {
      this.drawEngravingCenteredText(
        ctx,
        line,
        width / 2,
        startY + index * lineHeight
      );
    });
  }

  // Ink-bounds centering (measuring each line's actual visible glyph extent)
  // looks great per line, but decorative/cursive fonts have wildly different
  // left/right bearings depending on which specific letters are in a given
  // line - centering each line independently on its own ink made lines with
  // different text visibly misaligned from each other. Plain advance-width
  // centering treats every line identically, so multi-line messages stay
  // consistent with each other; the per-style horizontalNudge above already
  // corrects the overall left/right drift.
  drawEngravingCenteredText(ctx, text, centerX, y) {
    ctx.textAlign = 'center';
    ctx.fillText(text, centerX, y);
  }

  updateEngravingSaveState() {
    if (!this.engravingSave) return;

    this.engravingSave.disabled = false;
  }

  openEngravingModal(focusElement) {
    if (!this.engravingModal) return;

    this.engravingLineInputs.forEach((input, index) => {
      const hiddenInput = this.engravingHiddenLines[index];
      input.value = hiddenInput ? hiddenInput.value : '';
    });

    const savedStyle = (this.engravingHiddenStyle && this.engravingHiddenStyle.value) || 'block';
    const styleButton =
      this.engravingStyleButtons.find((button) => button.dataset.engravingStyleOption === savedStyle) ||
      this.engravingStyleButtons[0];
    if (styleButton) this.selectEngravingStyle(styleButton);

    this.resizeEngravingCanvas();
    this.drawEngravingPreview();
    this.updateEngravingSaveState();

    if (this.engravingModalRemove) {
      const hasExistingId = this.engravingHiddenId && this.engravingHiddenId.value;
      this.engravingModalRemove.hidden = !hasExistingId;
    }

    this.engravingModal.show(focusElement || this.engravingLineInputs[0], this.engravingLineInputs[0]);
  }

  closeEngravingModal() {
    if (this.engravingModal) this.engravingModal.hide();
  }

  engravingStorageKey(id) {
    return `engraving:${id}`;
  }

  saveEngravingData(id, data) {
    try {
      localStorage.setItem(this.engravingStorageKey(id), JSON.stringify(data));
    } catch (error) {
      // Storage unavailable (private browsing, quota, etc.) - the hidden
      // form fields already carry what's needed to add to cart, so editing
      // from the cart just won't be able to prefill in that case.
    }
  }

  readEngravingData(id) {
    try {
      const raw = localStorage.getItem(this.engravingStorageKey(id));
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  removeEngravingData(id) {
    try {
      localStorage.removeItem(this.engravingStorageKey(id));
    } catch (error) {
      // Ignore - nothing to clean up if storage isn't available.
    }
  }

  getLocalizedError(key) {
    const isArabic = document.documentElement.lang
      .toLowerCase()
      .startsWith('ar');

    const messages = {
      firstField: {
        en: 'Please enter text in the first field.',
        ar: 'يرجى إدخال النص في الحقل الأول.'
      },
      acceptTerms: {
        en: 'Please accept the terms and conditions.',
        ar: 'يرجى الموافقة على الشروط والأحكام.'
      },
      confirmBoth: {
        en: 'Please check both confirmation boxes before continuing.',
        ar: 'يرجى تحديد مربعي التأكيد قبل المتابعة.'
      }
    };

    const language = isArabic ? 'ar' : 'en';

    return messages[key]?.[language] || messages[key]?.en || '';
  }

  showEngravingError(message) {
    if (!this.engravingErrorBox) return;

    if (!message) {
      this.engravingErrorBox.hidden = true;
      this.engravingErrorBox.textContent = '';
      return;
    }

    this.engravingErrorBox.textContent = message;
    this.engravingErrorBox.hidden = false;
  }

  showEngravingTermsError(message) {
    if (!this.engravingTermsErrorBox) return;

    if (!message) {
      this.engravingTermsErrorBox.hidden = true;
      this.engravingTermsErrorBox.textContent = '';
      return;
    }

    this.engravingTermsErrorBox.textContent = message;
    this.engravingTermsErrorBox.hidden = false;
  }

  showEngravingReviewError(message) {
    if (!this.engravingReviewError) return;

    if (!message) {
      this.engravingReviewError.hidden = true;
      this.engravingReviewError.textContent = '';
      return;
    }

    this.engravingReviewError.textContent = message;
    this.engravingReviewError.hidden = false;
  }

  // Writes the hidden line-item property fields the product form submits,
  // and persists the same data to localStorage under a stable id
  // (`_Engraving Id`) so it can be reloaded if the customer later comes back
  // to edit this exact line from the cart. The separate "review before Add
  // to Cart" popup (see setupEngravingReview) is what actually gates
  // checkout - this just records what was composed here.
  saveEngraving() {

    this.showEngravingError(null);
    this.showEngravingTermsError(null);

    const values = this.engravingLineInputs.map((input) => input.value.trim());

    const termsAccepted = this.engravingTermsCheckbox
      ? this.engravingTermsCheckbox.checked
      : true;

    const firstFieldMissing = !values[0];
    const termsNotAccepted = !termsAccepted;

    if (firstFieldMissing) {
      this.showEngravingError(
        this.getLocalizedError('firstField')
      );
    }

    if (termsNotAccepted) {
      this.showEngravingTermsError(
        this.getLocalizedError('acceptTerms')
      );
    }

    if (firstFieldMissing || termsNotAccepted) {
      if (firstFieldMissing && this.engravingLineInputs[0]) {
        this.engravingLineInputs[0].focus();
      } else if (termsNotAccepted && this.engravingTermsCheckbox) {
        this.engravingTermsCheckbox.focus();
      }

      return;
    }

    this.engravingHiddenLines.forEach((hiddenInput, index) => {
      const value = values[index] || '';
      hiddenInput.value = value;
      hiddenInput.disabled = !value;
    });
    if (this.engravingHiddenStyle) {
      this.engravingHiddenStyle.value = this.engravingSelectedStyle;
      this.engravingHiddenStyle.disabled = false;
    }

    // Reuse the existing id when this is an edit of an already-confirmed
    // engraving, so the localStorage record updates in place instead of
    // forking into a second entry.
    const engravingId = (this.engravingHiddenId && this.engravingHiddenId.value) || `engraving-${Date.now()}`;
    if (this.engravingHiddenId) {
      this.engravingHiddenId.value = engravingId;
      this.engravingHiddenId.disabled = false;
    }

    const feeVariantId = this.dataset.engravingFeeVariantId;
    if (this.engravingHiddenFeeVariantId) {
      this.engravingHiddenFeeVariantId.value = feeVariantId || '';
      this.engravingHiddenFeeVariantId.disabled = !feeVariantId;
    }

    this.saveEngravingData(engravingId, {
      line1: values[0] || '',
      line2: values[1] || '',
      line3: values[2] || '',
      style: this.engravingSelectedStyle,
      productId: this.dataset.productId || '',
      updatedAt: Date.now(),
    });

    if (this.engravingValue) {
      this.engravingValue.textContent = values.filter(Boolean).join(', ');
    }

    const engravingFeeTitle = this.querySelector('[data-engraving-fee-title]');

    const firstAvailableValue = values.find((value) => value);

    if (engravingFeeTitle && firstAvailableValue) {
      engravingFeeTitle.textContent = firstAvailableValue;
    }

    if (this.engravingRow) this.engravingRow.hidden = true;
    if (this.engravingSummary) this.engravingSummary.hidden = false;
    this.closeEngravingModal();
  }

  clearEngraving() {
    if (this.engravingHiddenId && this.engravingHiddenId.value) {
      this.removeLinkedEngravingFee(this.engravingHiddenId.value);
      this.removeEngravingData(this.engravingHiddenId.value);
    }
    this.engravingHiddenLines.forEach((hiddenInput) => {
      hiddenInput.value = '';
      hiddenInput.disabled = true;
    });
    if (this.engravingHiddenStyle) {
      this.engravingHiddenStyle.value = '';
      this.engravingHiddenStyle.disabled = true;
    }
    if (this.engravingHiddenId) {
      this.engravingHiddenId.value = '';
      this.engravingHiddenId.disabled = true;
    }
    if (this.engravingHiddenFeeVariantId) {
      this.engravingHiddenFeeVariantId.value = '';
      this.engravingHiddenFeeVariantId.disabled = true;
    }
    this.engravingLineInputs.forEach((input) => (input.value = ''));
    if (this.engravingTermsCheckbox) this.engravingTermsCheckbox.checked = false;
    this.drawEngravingPreview();
    if (this.engravingRow) this.engravingRow.hidden = false;
    if (this.engravingSummary) this.engravingSummary.hidden = true;
  }

  async removeLinkedEngravingFee(engravingId) {
    if (!engravingId) return;
    try {
      const cartRes = await fetch(window.Shopify.routes.root + 'cart.js');
      const cartData = await cartRes.json();

      const updates = {};
      let hasUpdate = false;

      cartData.items.forEach((item) => {
        if (item.properties && item.properties['_Linked Engraving Id'] === engravingId) {
          updates[item.key] = 0;
          hasUpdate = true;
        }
      });

      if (hasUpdate) {
        await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ updates })
        });

        const cartElement = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
        if (cartElement && cartElement.updateCart) {
          cartElement.updateCart();
        } else {
          window.location.reload();
        }
      }
    } catch (e) {
      console.error('Failed to remove linked engraving fee', e);
    }
  }

  // Separate popup from the compose drawer above - gates the main product
  // form's Add to Cart submit itself (see product-form.js, which calls
  // engravingNeedsReview()/openEngravingReviewModal() on this element before
  // it will actually submit), rather than being part of saving the
  // engraving. Every Add to Cart click while an engraving is set re-opens
  // this and requires both boxes checked again, however many times the
  // customer clicks.
  setupEngravingReview() {
    this.engravingReviewModal = this.querySelector('[data-engraving-review-modal]');
    this.engravingReviewOverlay = this.querySelector('[data-engraving-review-overlay]');
    this.engravingReviewImage = this.engravingReviewModal
      ? this.engravingReviewModal.querySelector('[data-engraving-review-image]')
      : null;
    this.engravingReviewLines = this.engravingReviewModal
      ? this.engravingReviewModal.querySelector('[data-engraving-review-lines]')
      : null;
    this.engravingReviewEditButton = this.engravingReviewModal
      ? this.engravingReviewModal.querySelector('[data-engraving-review-edit]')
      : null;
    this.engravingReviewTextCheckbox = this.engravingReviewModal
      ? this.engravingReviewModal.querySelector('[data-engraving-review-text-checkbox]')
      : null;
    this.engravingReviewPolicyCheckbox = this.engravingReviewModal
      ? this.engravingReviewModal.querySelector('[data-engraving-review-policy-checkbox]')
      : null;
    this.engravingReviewConfirmButton = this.engravingReviewModal
      ? this.engravingReviewModal.querySelector('[data-engraving-review-confirm]')
      : null;
    this.engravingReviewError = this.engravingReviewModal
      ? this.engravingReviewModal.querySelector('[data-engraving-review-error]')
      : null;
    // Set while the review popup is open - the real Add to Cart submit
    // logic (from product-form.js), invoked only once the customer confirms.
    this.pendingEngravingReviewConfirm = null;

    if (!this.engravingReviewModal) return;

    this.relocateDrawer(this.engravingReviewModal, this.engravingReviewOverlay);

    if (this.engravingReviewTextCheckbox) {
      this.engravingReviewTextCheckbox.addEventListener('change', () => {
        if (this.engravingReviewTextCheckbox.checked) {
          this.showEngravingReviewError(null);
        }

        this.updateEngravingReviewConfirmState();
      });
    }

    if (this.engravingReviewPolicyCheckbox) {
      this.engravingReviewPolicyCheckbox.addEventListener('change', () => {
        if (this.engravingReviewPolicyCheckbox.checked) {
          this.showEngravingReviewError(null);
        }

        this.updateEngravingReviewConfirmState();
      });
    }
    if (this.engravingReviewConfirmButton) {
      this.engravingReviewConfirmButton.addEventListener('click', () => this.confirmEngravingReview());
    }
    if (this.engravingReviewEditButton) {
      this.engravingReviewEditButton.addEventListener('click', () => {
        this.cancelEngravingReview();
        this.openEngravingModal(this.engravingEdit || this.engravingToggle);
      });
    }

    this.engravingReviewModal.querySelectorAll('[data-engraving-review-close]').forEach((element) => {
      element.addEventListener('click', () => this.cancelEngravingReview());
    });
    if (this.engravingReviewOverlay) {
      this.engravingReviewOverlay.addEventListener('click', () => this.cancelEngravingReview());
    }
  }

  // Called by product-form.js before it submits the Add to Cart form - true
  // whenever this product currently has a saved engraving.
  engravingNeedsReview() {
    return this.engravingHiddenLines.some((input) => input.value.trim() !== '');
  }

  // Opens the review popup and defers the actual Add to Cart submission
  // (onConfirmed, product-form.js's real submit logic) until the customer
  // explicitly re-checks both boxes here. Reuses a still-image snapshot of
  // the compose canvas (already drawn when the engraving was saved) rather
  // than a second live canvas.
  openEngravingReviewModal(onConfirmed) {
    if (!this.engravingReviewModal) {
      onConfirmed();
      return;
    }

    this.pendingEngravingReviewConfirm = onConfirmed;

    if (this.engravingReviewLines) {
      const feeLi = this.engravingReviewLines.querySelector('.engraving-confirm-lines__fee');
      this.engravingReviewLines.innerHTML = '';
      if (feeLi) {
        this.engravingReviewLines.appendChild(feeLi);
      }
      this.engravingHiddenLines
        .map((input) => input.value.trim())
        .filter((value) => value !== '')
        .forEach((value) => {
          const item = document.createElement('li');
          item.textContent = value;
          this.engravingReviewLines.appendChild(item);
        });
      const styleLabel = this.engravingStyleName ? this.engravingStyleName.textContent.trim() : '';
      if (styleLabel) {
        const styleItem = document.createElement('li');
        styleItem.className = 'engraving-confirm-lines__style';
        styleItem.textContent = styleLabel;
        this.engravingReviewLines.appendChild(styleItem);
      }
    }

    if (this.engravingReviewImage) {
      let dataUrl = '';
      try {
        dataUrl = this.engravingCanvas ? this.engravingCanvas.toDataURL('image/png') : '';
      } catch (error) {
        dataUrl = '';
      }
      if (dataUrl && dataUrl !== 'data:,') {
        this.engravingReviewImage.src = dataUrl;
        this.engravingReviewImage.hidden = false;
      } else {
        this.engravingReviewImage.hidden = true;
      }
    }

    // Require a fresh confirmation on every single Add to Cart click.
    if (this.engravingReviewTextCheckbox) this.engravingReviewTextCheckbox.checked = false;
    if (this.engravingReviewPolicyCheckbox) this.engravingReviewPolicyCheckbox.checked = false;
    this.updateEngravingReviewConfirmState();

    this.engravingReviewModal.show(this.engravingReviewTextCheckbox, this.engravingReviewTextCheckbox);
  }

  updateEngravingReviewConfirmState() {
    if (!this.engravingReviewConfirmButton) return;

    this.engravingReviewConfirmButton.disabled = false;
  }

  confirmEngravingReview() {
    const textConfirmed = this.engravingReviewTextCheckbox
      ? this.engravingReviewTextCheckbox.checked
      : true;

    const policyConfirmed = this.engravingReviewPolicyCheckbox
      ? this.engravingReviewPolicyCheckbox.checked
      : true;

    if (!textConfirmed || !policyConfirmed) {
      this.showEngravingReviewError(
        this.getLocalizedError('confirmBoth')
      );

      if (!textConfirmed) {
        this.engravingReviewTextCheckbox?.focus();
      } else {
        this.engravingReviewPolicyCheckbox?.focus();
      }

      return;
    }

    this.showEngravingReviewError(null);

    const onConfirmed = this.pendingEngravingReviewConfirm;
    this.pendingEngravingReviewConfirm = null;

    if (this.engravingReviewModal) {
      this.engravingReviewModal.hide();
    }

    if (onConfirmed) onConfirmed();
  }

  cancelEngravingReview() {
    this.pendingEngravingReviewConfirm = null;
    if (this.engravingReviewModal) this.engravingReviewModal.hide();
  }

  // The chosen wrap product is added to the cart as its own line (see
  // product-form.js), alongside line item properties describing it on the
  // main product's line. Selecting a wrapping paper here only records which
  // product/price/pair id to use; nothing is added to the cart until the
  // customer submits the actual product form.
  setupGiftWrap() {
    this.giftwrapRow = this.querySelector('[data-giftwrap-row]');
    this.giftwrapToggleButton = this.querySelector('[data-giftwrap-toggle]');
    this.giftwrapSummary = this.querySelector('[data-giftwrap-summary]');
    this.giftwrapValue = this.querySelector('[data-giftwrap-value]');
    this.giftwrapRemove = this.querySelector('[data-giftwrap-remove]');
    this.giftwrapEdit = this.querySelector('[data-giftwrap-edit]');
    this.giftwrapModal = this.querySelector('[data-giftwrap-modal]');
    this.giftwrapOverlay = this.querySelector('[data-giftwrap-overlay]');
    this.giftwrapSave = this.giftwrapModal ? this.giftwrapModal.querySelector('[data-giftwrap-save]') : null;
    this.giftwrapErrorBox = this.giftwrapModal ? this.giftwrapModal.querySelector('[data-giftwrap-error]') : null;
    this.giftwrapMessageErrorBox = this.giftwrapModal
      ? this.giftwrapModal.querySelector('[data-giftwrap-message-error]')
      : null;
    this.giftwrapOptionButtons = this.giftwrapModal
      ? Array.from(this.giftwrapModal.querySelectorAll('[data-giftwrap-option]'))
      : [];
    this.giftwrapToInput = this.giftwrapModal ? this.giftwrapModal.querySelector('[data-giftwrap-to]') : null;
    this.giftwrapMessageInput = this.giftwrapModal
      ? this.giftwrapModal.querySelector('[data-giftwrap-message]')
      : null;
    this.giftwrapFromInput = this.giftwrapModal ? this.giftwrapModal.querySelector('[data-giftwrap-from]') : null;
    this.giftwrapCounter = this.giftwrapModal ? this.giftwrapModal.querySelector('[data-giftwrap-counter]') : null;
    this.giftwrapHiddenPaper = this.querySelector('[data-giftwrap-hidden-paper]');
    this.giftwrapHiddenPrice = this.querySelector('[data-giftwrap-hidden-price]');
    this.giftwrapHiddenTo = this.querySelector('[data-giftwrap-hidden-to]');
    this.giftwrapHiddenMessage = this.querySelector('[data-giftwrap-hidden-message]');
    this.giftwrapHiddenFrom = this.querySelector('[data-giftwrap-hidden-from]');
    this.giftwrapHiddenId = this.querySelector('[data-giftwrap-hidden-id]');
    this.giftwrapHiddenVariantId = this.querySelector('[data-giftwrap-hidden-variant-id]');
    this.giftwrapSelectedVariantId = null;
    this.giftwrapSwiperContainer = this.querySelector('[data-giftwrap-swiper]');

    if (!this.giftwrapToggleButton) return;

    this.relocateDrawer(this.giftwrapModal, this.giftwrapOverlay);
    this.initGiftWrapSwiper();

    this.giftwrapToggleButton.addEventListener('click', (event) => this.openGiftWrapModal(event.currentTarget));
    if (this.giftwrapEdit)
      this.giftwrapEdit.addEventListener('click', (event) => this.openGiftWrapModal(event.currentTarget));
    if (this.giftwrapRemove) this.giftwrapRemove.addEventListener('click', () => this.clearGiftWrap());
    if (this.giftwrapSave) this.giftwrapSave.addEventListener('click', () => this.saveGiftWrap());

    // The wrapping-paper options are plain <div>s (Swiper slides), not
    // <button>s, so click alone isn't enough for keyboard users - Enter/Space
    // needs to trigger the same selection a native button would give free.
    this.giftwrapOptionButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectGiftWrapOption(button));
      button.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
          event.preventDefault();
          this.selectGiftWrapOption(button);
        }
      });
    });

    if (this.giftwrapMessageInput) {
      this.giftwrapMessageInput.addEventListener('input', () => {
        this.showGiftWrapMessageError(null);
        this.updateGiftWrapCounter();
        this.updateGiftWrapSaveState();
      });
    }
    if (this.giftwrapToInput) this.giftwrapToInput.addEventListener('input', () => this.updateGiftWrapSaveState());

    this.updateGiftWrapCounter();
    this.updateGiftWrapSaveState();

    if (this.giftwrapModal) {
      this.giftwrapModal.querySelectorAll('[data-giftwrap-modal-close]').forEach((element) => {
        element.addEventListener('click', () => this.closeGiftWrapModal());
      });
    }
    if (this.giftwrapOverlay) {
      this.giftwrapOverlay.addEventListener('click', () => this.closeGiftWrapModal());
    }
  }

  // Same Swiper setup as the featured-collection slider (see
  // sections/featured-collection.liquid) - free-dragging slides sized by
  // their own CSS width, with the theme's existing .fc-scrollbar styling
  // reused as-is so the scrollbar matches exactly, not just visually.
  initGiftWrapSwiper() {
    if (!this.giftwrapSwiperContainer || this.giftwrapSwiperContainer.swiper) return;

    this.giftwrapSwiper = new Swiper(this.giftwrapSwiperContainer, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: true,
      grabCursor: true,
      watchOverflow: true,
      a11y: false,

      mousewheel: {
        forceToAxis: true,
      },

      scrollbar: {
        el: this.giftwrapSwiperContainer.querySelector('.fc-scrollbar'),
        draggable: true,
      },
    });
  }

  findGiftWrapOptionByVariantId(variantId) {
    return this.giftwrapOptionButtons.find((button) => String(button.dataset.variantId) === String(variantId));
  }

  getGiftWrapErrorMessage(type) {
    if (!this.giftwrapErrorBox) return '';
    const map = {
      generic: this.giftwrapErrorBox.dataset.errorGeneric,
      selectPaper: this.giftwrapErrorBox.dataset.errorSelectPaper,
      messageRequired: this.giftwrapErrorBox.dataset.errorMessageRequired,
    };
    return map[type] || '';
  }

  showGiftWrapError(message) {
    if (!this.giftwrapErrorBox) return;
    if (!message) {
      this.giftwrapErrorBox.hidden = true;
      this.giftwrapErrorBox.textContent = '';
      return;
    }
    this.giftwrapErrorBox.textContent = message;
    this.giftwrapErrorBox.hidden = false;
  }

  showGiftWrapMessageError(message) {
    if (!this.giftwrapMessageErrorBox) return;

    if (!message) {
      this.giftwrapMessageErrorBox.hidden = true;
      this.giftwrapMessageErrorBox.textContent = '';
      return;
    }

    this.giftwrapMessageErrorBox.textContent = message;
    this.giftwrapMessageErrorBox.hidden = false;
  }

  // Checkbox behavior: clicking the already-selected paper unchecks it
  // (clears the selection) instead of leaving it stuck selected the way a
  // radio button would. Clicking a different paper still swaps the
  // selection to it, since only one wrapping paper applies at a time.
  selectGiftWrapOption(button) {
    const isAlreadySelected = button.dataset.variantId === this.giftwrapSelectedVariantId;
    this.giftwrapSelectedVariantId = isAlreadySelected ? null : button.dataset.variantId;
    this.giftwrapOptionButtons.forEach((optionButton) => {
      const isSelected = !isAlreadySelected && optionButton === button;
      optionButton.classList.toggle('is-selected', isSelected);
      optionButton.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });
    this.showGiftWrapError(null);
    this.updateGiftWrapSaveState();
  }

  updateGiftWrapCounter() {
    if (!this.giftwrapMessageInput || !this.giftwrapCounter) return;
    const max = this.giftwrapMessageInput.maxLength > 0 ? this.giftwrapMessageInput.maxLength : 200;
    const remaining = Math.max(max - this.giftwrapMessageInput.value.length, 0);
    // Keep whatever the server rendered (respects the store's locale) and
    // just swap the leading number, instead of hardcoding "Characters left".
    if (!this.giftwrapCounter.dataset.template) {
      this.giftwrapCounter.dataset.template = this.giftwrapCounter.textContent;
    }
    this.giftwrapCounter.textContent = this.giftwrapCounter.dataset.template.replace(/\d+/, remaining);
  }

  updateGiftWrapSaveState() {
    if (!this.giftwrapSave) return;

    this.giftwrapSave.disabled = false;
  }

  openGiftWrapModal(focusElement) {
    if (!this.giftwrapModal) return;
    this.showGiftWrapError(null);

    // Repopulate from whatever's already been saved (hidden inputs), so
    // editing shows the previous selection instead of a blank form.
    if (this.giftwrapToInput) this.giftwrapToInput.value = this.giftwrapHiddenTo ? this.giftwrapHiddenTo.value : '';
    if (this.giftwrapMessageInput)
      this.giftwrapMessageInput.value = this.giftwrapHiddenMessage ? this.giftwrapHiddenMessage.value : '';
    if (this.giftwrapFromInput)
      this.giftwrapFromInput.value = this.giftwrapHiddenFrom ? this.giftwrapHiddenFrom.value : '';

    const savedPaperTitle = this.giftwrapHiddenPaper ? this.giftwrapHiddenPaper.value : '';
    const matchingOption = savedPaperTitle
      ? this.giftwrapOptionButtons.find((button) => button.dataset.productTitle === savedPaperTitle)
      : null;
    if (matchingOption) {
      // Reset first so selectGiftWrapOption's toggle check (which unchecks
      // an already-selected paper) always treats this as a fresh selection
      // rather than a re-click that would uncheck it.
      this.giftwrapSelectedVariantId = null;
      this.selectGiftWrapOption(matchingOption);
    } else {
      this.giftwrapSelectedVariantId = null;
      this.giftwrapOptionButtons.forEach((button) => {
        button.classList.remove('is-selected');
        button.setAttribute('aria-checked', 'false');
      });
    }

    this.updateGiftWrapCounter();
    this.updateGiftWrapSaveState();
    this.giftwrapModal.show(focusElement || this.giftwrapOptionButtons[0], this.giftwrapOptionButtons[0]);

    // The drawer is off-screen (translateX) but still in layout when Swiper
    // first measured slide widths at page load, so its cached sizes should
    // already be correct - but re-measure on every open anyway, since it's
    // cheap and guarantees the slider reflects the current (185px desktop /
    // 88px mobile) card size instead of anything stale from an earlier
    // viewport or layout pass.
    if (this.giftwrapSwiper) {
      requestAnimationFrame(() => this.giftwrapSwiper.update());
    }
  }

  closeGiftWrapModal() {
    if (this.giftwrapModal) this.giftwrapModal.hide();
  }

  saveGiftWrap() {
    this.showGiftWrapError(null);
    this.showGiftWrapMessageError(null);

    const paperNotSelected = !this.giftwrapSelectedVariantId;

    const message = this.giftwrapMessageInput
      ? this.giftwrapMessageInput.value.trim()
      : '';

    const messageMissing = !message;

    if (paperNotSelected) {
      this.showGiftWrapError(
        this.getGiftWrapErrorMessage('selectPaper')
      );
    }

    if (messageMissing) {
      this.showGiftWrapMessageError(
        this.getGiftWrapErrorMessage('messageRequired')
      );
    }

    if (paperNotSelected || messageMissing) {
      if (paperNotSelected) {
        this.giftwrapOptionButtons[0]?.focus();
      } else if (messageMissing && this.giftwrapMessageInput) {
        this.giftwrapMessageInput.focus();
      }

      return;
    }

    const selectedOption = this.findGiftWrapOptionByVariantId(this.giftwrapSelectedVariantId);
    const paperTitle = selectedOption ? selectedOption.dataset.productTitle : '';
    const priceText = selectedOption ? selectedOption.dataset.priceText : '';
    const to = this.giftwrapToInput ? this.giftwrapToInput.value.trim() : '';
    const from = this.giftwrapFromInput ? this.giftwrapFromInput.value.trim() : '';

    this.setGiftWrapHiddenValue(this.giftwrapHiddenPaper, paperTitle);
    this.setGiftWrapHiddenValue(this.giftwrapHiddenPrice, priceText);
    this.setGiftWrapHiddenValue(this.giftwrapHiddenTo, to);
    this.setGiftWrapHiddenValue(this.giftwrapHiddenMessage, message);
    this.setGiftWrapHiddenValue(this.giftwrapHiddenFrom, from);

    // Reuse the existing pair id if the customer is just editing an
    // already-saved selection, so re-saving doesn't orphan the wrap line
    // item that's already tied to it. Only mint a fresh one the first time.
    const pairId = (this.giftwrapHiddenId && this.giftwrapHiddenId.value) || `giftwrap-${Date.now()}`;
    this.setGiftWrapHiddenValue(this.giftwrapHiddenId, pairId);
    this.setGiftWrapHiddenValue(this.giftwrapHiddenVariantId, this.giftwrapSelectedVariantId);

    if (this.giftwrapValue) {
      this.giftwrapValue.textContent = priceText ? `${paperTitle} | ${priceText}` : paperTitle;
    }
    if (this.giftwrapRow) this.giftwrapRow.hidden = true;
    if (this.giftwrapSummary) this.giftwrapSummary.hidden = false;
    this.closeGiftWrapModal();
  }

  setGiftWrapHiddenValue(input, value) {
    if (!input) return;
    input.value = value || '';
    input.disabled = !value;
  }

  clearGiftWrap() {
    this.setGiftWrapHiddenValue(this.giftwrapHiddenPaper, '');
    this.setGiftWrapHiddenValue(this.giftwrapHiddenPrice, '');
    this.setGiftWrapHiddenValue(this.giftwrapHiddenTo, '');
    this.setGiftWrapHiddenValue(this.giftwrapHiddenMessage, '');
    this.setGiftWrapHiddenValue(this.giftwrapHiddenFrom, '');
    this.setGiftWrapHiddenValue(this.giftwrapHiddenId, '');
    this.setGiftWrapHiddenValue(this.giftwrapHiddenVariantId, '');

    this.giftwrapSelectedVariantId = null;
    if (this.giftwrapToInput) this.giftwrapToInput.value = '';
    if (this.giftwrapMessageInput) this.giftwrapMessageInput.value = '';
    if (this.giftwrapFromInput) this.giftwrapFromInput.value = '';
    this.giftwrapOptionButtons.forEach((button) => {
      button.classList.remove('is-selected');
      button.setAttribute('aria-checked', 'false');
    });
    this.updateGiftWrapCounter();

    if (this.giftwrapRow) this.giftwrapRow.hidden = false;
    if (this.giftwrapSummary) this.giftwrapSummary.hidden = true;
  }

  // The drawer/overlay use `position: fixed`, but a scroll-reveal animation on
  // an ancestor leaves a lingering `transform` that turns it into the
  // containing block for fixed descendants. Move both to <body>, matching how
  // the product availability drawer relocates itself, so they measure against
  // the real viewport.
  relocateDrawer(drawer, overlay) {
    if (!drawer || drawer.parentElement === document.body) return;
    document.body.appendChild(drawer);
    if (overlay) drawer.after(overlay);
  }
}

if (!customElements.get('gift-options')) {
  customElements.define('gift-options', GiftOptions);
}

// engraving.liquid uses <engraving-options> and gift_wrapping.liquid uses
// <gift-wrapping-options> — both need the same GiftOptions behaviour.
if (!customElements.get('engraving-options')) {
  customElements.define('engraving-options', class EngravingOptions extends GiftOptions { });
}
if (!customElements.get('gift-wrapping-options')) {
  customElements.define('gift-wrapping-options', class GiftWrappingOptions extends GiftOptions { });
}

// `gift-option-drawer` (the shared open/close/focus-trap drawer chrome used
// throughout this file) now lives in gift-option-drawer.js, loaded
// alongside this file - see that file for why it was split out.
